/**
 * Content Libraries v2 — one library and everything you can do inside it.
 *
 * `studio.ts` covers the listing and create; this covers the detail surface. Same API
 * family, deliberately separate file: the list page needs two calls, the detail page
 * needs ten, and none of them are useful anywhere else.
 *
 * NO LEGACY LIBRARY API. `/api/libraries/v1/` (ModuleStore `library-v1:` blocks) is a
 * different data model with a different key format; mixing the two produces keys the
 * other half of the app cannot resolve.
 *
 * NO OLX WRITES HERE, deliberately. `POST blocks/{key}/olx/` exists and this file used to
 * use it as "the only way to edit a block", which was wrong twice: the v2 XBlock runtime
 * serves both a real editor and a field-level write for the same block, and those are what
 * the platform's own authoring frontend uses. Editing lives in `editor.ts` for both kinds of
 * component now; what stays here is the library itself and the shape of its contents.
 *
 * Every path and field below was read off the running platform at release/verawood.1:
 *   openedx/core/djangoapps/content_libraries/urls.py                 (routes)
 *   openedx/core/djangoapps/content_libraries/rest_api/libraries.py   (detail, commit, types)
 *   openedx/core/djangoapps/content_libraries/rest_api/blocks.py      (blocks)
 *   openedx/core/djangoapps/content_libraries/rest_api/serializers.py (field names)
 *   openedx/core/djangoapps/content_libraries/constants.py            (licence values)
 */

import { openedx } from './openedx';
import type { StudioLibrary } from './studio';

const V2 = '/api/libraries/v2';

/**
 * Library and usage keys contain ':' (and course-derived keys '+'), which are legal in a
 * path segment but ambiguous in one. Encoding is safe: Django percent-decodes
 * `PATH_INFO` before URL resolution, and the route uses `<str:...>`, which matches
 * anything but '/'.
 */
const seg = (key: string) => encodeURIComponent(key);

/* --------------------------------------------------------------------- licences ---- */

/**
 * The platform's `LICENSE_OPTIONS`, values exactly as the API stores them.
 *
 * `license` is a DRF `ChoiceField`, so anything not in this list is a 400 — and note
 * that "all rights reserved" is the EMPTY STRING, not a slug. It is the one value most
 * likely to be guessed wrong.
 */
export const LIBRARY_LICENCES: [string, string][] = [
  ['', 'All rights reserved'],
  ['CC:4.0:BY', 'Creative Commons Attribution 4.0'],
  ['CC:4.0:BY:NC', 'Creative Commons Attribution-NonCommercial 4.0'],
  ['CC:4.0:BY:NC:ND', 'Creative Commons Attribution-NonCommercial-NoDerivatives 4.0'],
  ['CC:4.0:BY:NC:SA', 'Creative Commons Attribution-NonCommercial-ShareAlike 4.0'],
  ['CC:4.0:BY:ND', 'Creative Commons Attribution-NoDerivatives 4.0'],
  ['CC:4.0:BY:SA', 'Creative Commons Attribution-ShareAlike 4.0'],
];

export function licenceName(value: string): string {
  return LIBRARY_LICENCES.find(([v]) => v === value)?.[1] ?? value;
}

/* ------------------------------------------------------------------ the library ---- */

/**
 * `ContentLibraryMetadataSerializer` — the same serializer the listing returns, plus the
 * draft-authorship fields, which is why this extends the list type rather than
 * redeclaring it.
 */
export interface LibraryDetail extends StudioLibrary {
  created: string | null;
  last_draft_created: string | null;
  last_draft_created_by: string | null;
}

export const getLibrary = (libKey: string) =>
  openedx.get<LibraryDetail>(`${V2}/${seg(libKey)}/`);

/**
 * `ContentLibraryUpdateSerializer`, applied with `partial=True` — send only what changed.
 *
 * `title` and `description` are plain `CharField`s with no `allow_blank`, so an empty
 * string is a 400 rather than a clear. Callers must not offer "save an empty title" as
 * if it would work.
 */
export interface LibraryUpdate {
  title?: string;
  description?: string;
  license?: string;
  allow_public_read?: boolean;
  allow_public_learning?: boolean;
}

export const updateLibrary = (libKey: string, patch: LibraryUpdate) =>
  openedx.patch<LibraryDetail>(`${V2}/${seg(libKey)}/`, patch);

/* -------------------------------------------------------------------- publishing ---- */

/**
 * Publish every pending draft change in the library, or throw them all away.
 *
 * Library-wide, not per block: `commit/` is all-or-nothing, so a library holding a
 * drafted deletion cannot publish an unrelated edit without also publishing that
 * deletion. The UI has to say so before the author commits.
 */
export const publishLibrary = (libKey: string) =>
  openedx.post<Record<string, never>>(`${V2}/${seg(libKey)}/commit/`, {});

export const discardLibraryChanges = (libKey: string) =>
  openedx.delete<Record<string, never>>(`${V2}/${seg(libKey)}/commit/`);

/* ----------------------------------------------------------------- block types ---- */

export interface LibraryBlockType {
  block_type: string;
  display_name: string;
}

/**
 * Which XBlock types this library accepts.
 *
 * Always ask; never hardcode. The list is the intersection of installed XBlocks and the
 * platform's advanced-module settings, so it differs between installs and changes when
 * an operator installs a block.
 */
export const listBlockTypes = (libKey: string) =>
  openedx.get<LibraryBlockType[]>(`${V2}/${seg(libKey)}/block_types/`);

/**
 * What a block type can do, which `block_types/` does not say.
 *
 * Its payload is `{block_type, display_name}` and nothing else, so the list above cannot
 * answer the one question a library palette has to ask: does this type hold children?
 * A type that does is REFUSED by every library — the platform answers the create with
 * "has children, so it is not supported in content libraries" — and `conditional` was
 * offered in every library on this install for exactly that reason.
 *
 * Answered by our own authoring app off the installed XBlock class (`has_children`), so it
 * stays a question about the platform rather than a list of type names that would be right
 * today and silently wrong after an upgrade. Platform-wide, hence no library key: the same
 * answer for every library, cacheable for the session.
 */
export interface BlockTypeCapability {
  block_type: string;
  /** True for a container. A library cannot store one. */
  has_children: boolean;
}

export const listBlockTypeCapabilities = () =>
  openedx.get<BlockTypeCapability[]>('/api/authoring/v1/block-types');

/* ---------------------------------------------------------------------- blocks ---- */

/** `LibraryXBlockMetadataSerializer` (a `PublishableItemSerializer`). */
export interface LibraryBlock {
  /** The usage key, e.g. `lb:Org:lib-slug:problem:my-block`. Serialised as `id`. */
  id: string;
  block_type: string;
  display_name: string;
  published_display_name: string | null;
  has_unpublished_changes: boolean;
  last_published: string | null;
  published_by: string | null;
  last_draft_created: string | null;
  last_draft_created_by: string | null;
  created: string | null;
  created_by: string | null;
  /** When the current draft was written — "last edited" as an author means it. */
  modified: string | null;
  tags_count: number;
  can_stand_alone: boolean;
  collections?: { key: string; title: string }[];
}

/** edx-platform's `DefaultPagination`: `count` plus page bookkeeping, not just `next`. */
export interface Page<T> {
  results: T[];
  count: number;
  num_pages: number;
  current_page: number;
  next: string | null;
  previous: string | null;
}

/**
 * List blocks. ALWAYS paginated, unlike the library listing — that one only paginates
 * when asked with `?pagination=true`, this one has no unpaginated form. Default page
 * size is 10 and the server caps `page_size` at 100.
 */
export function listBlocks(
  libKey: string,
  params: { page?: number; pageSize?: number; search?: string; blockType?: string } = {},
) {
  const query = new URLSearchParams();
  if (params.page && params.page > 1) query.set('page', String(params.page));
  if (params.pageSize) query.set('page_size', String(params.pageSize));
  if (params.search) query.set('text_search', params.search);
  if (params.blockType) query.set('block_type', params.blockType);
  const suffix = query.toString() ? `?${query}` : '';

  return openedx.get<Page<LibraryBlock>>(`${V2}/${seg(libKey)}/blocks/${suffix}`);
}

/**
 * An id for a block about to be created.
 *
 * `definition_id` is misleadingly named — it is the slug that becomes the last segment of the
 * usage key, and it must be unique in the library. It is also PERMANENT: it shows up in URLs
 * and exports forever, and there is no endpoint to change it.
 *
 * That is exactly why it is generated rather than derived from a title the author typed. A
 * title is something people rewrite; welding the first draft of one into an identifier means
 * a block called "Draft 1" answers to `draft-1` for the rest of its life. The platform's own
 * authoring frontend uses a bare `uuid4()` here; this keeps the type in front of it so a key
 * still reads as something, and takes 4 random bytes rather than 16 because uniqueness is
 * only needed inside one library.
 */
export function newBlockId(blockType: string): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const suffix = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${blockType}-${suffix}`;
}

export const createBlock = (libKey: string, blockType: string, definitionId: string) =>
  openedx.post<LibraryBlock>(`${V2}/${seg(libKey)}/blocks/`, {
    block_type: blockType,
    definition_id: definitionId,
  });

/**
 * Delete a block. Note the path: NOT nested under the library — the usage key already
 * identifies it, so this hangs off `/blocks/{usage_key}/`.
 *
 * This is a DRAFT deletion. The block stays live for every course using the library
 * until someone publishes, which is why the publish control has to name deletions.
 */
export const deleteBlock = (usageKey: string) =>
  openedx.delete<Record<string, never>>(`${V2}/blocks/${seg(usageKey)}/`);

/* ------------------------------------------------------------------------ errors ---- */

/**
 * Turn whatever the server said into a sentence.
 *
 * DRF returns `{field: ["message"]}` for validation errors and `{detail: "..."}` for
 * everything else. Showing "400" instead of "That slug is already used" is the
 * difference between an author fixing it and an author filing a bug.
 */
export function apiMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (typeof record['detail'] === 'string') return record['detail'];

  for (const value of Object.values(record)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return null;
}
